import { useCallback, useEffect, useState } from 'react';

const LS_DISMISSED = 'comms:installDismissed';

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => window.matchMedia?.('(max-width: 768px)').matches;

// Install-to-home-screen prompt state (Android one-tap prompt, iOS manual guide).
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(LS_DISMISSED) === '1');
  const [mobile, setMobile] = useState(isMobile);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    const mq = window.matchMedia?.('(max-width: 768px)');
    const onResize = () => setMobile(mq.matches);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    mq?.addEventListener?.('change', onResize);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      mq?.removeEventListener?.('change', onResize);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return 'unavailable';
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === 'accepted') setInstalled(true);
    return outcome;
  }, [deferred]);

  const dismiss = useCallback(() => {
    localStorage.setItem(LS_DISMISSED, '1');
    setDismissed(true);
  }, []);

  const ios = isIOS();
  return {
    visible: mobile && !installed && !dismissed && (Boolean(deferred) || ios),
    canPrompt: Boolean(deferred),
    ios,
    installed,
    install,
    dismiss,
  };
}
